import api from "../../services/baseapi";

// ── Enums ─────────────────────────────────────────────────────────────────────

export type ScorecardStatus = "draft" | "published" | "outdated";
export type ScoringMode = "weighted_average" | "equal_weight";
export type TrafficLight = "green" | "yellow" | "red" | null;
export type UpdateMode = "live" | "snapshot" | "manual";
export type ValueSource = "manual" | "formula" | "api";
export type ValueScale =
  | "percentage"
  | "score_0_10"
  | "score_1_5"
  | "count"
  | "custom";
export type BenchmarkSource = "manual" | "library";
export type ApprovalStatus = "pending" | "accepted" | "rejected";

// ── Dynamic range (score bands) ───────────────────────────────────────────────

export interface DynamicRangeBand {
  from: number;
  to: number;
  label: string;
  color: string;
  description?: string;
}

// ── Live diff ─────────────────────────────────────────────────────────────────

export interface LiveDiff {
  has_unpublished_changes: boolean;
  changed_indicator_count: number;
  published_overall_score: number | null;
  live_overall_score: number | null;
  overall_delta: number | null;
  domains: Array<{
    id: string;
    name: string;
    published_score: number | null;
    live_score: number | null;
    delta: number | null;
    changed: boolean;
  }>;
  changed_indicators: Array<{
    id: string;
    name: string;
    domain_name: string;
    published_value: number | null;
    live_value: number | null;
    delta: number | null;
  }>;
}

// ── Core types ────────────────────────────────────────────────────────────────

export interface ApiScorecard {
  id: string;
  project_id: string;
  title: string;
  scoring_mode: ScoringMode;
  status: ScorecardStatus;
  overall_score: number | null;
  overall_traffic_light: TrafficLight;
  overall_benchmark_upper: number | null;
  overall_benchmark_lower: number | null;
  published_at: string | null;
  published_by: number | null;
  summary: {
    green: number;
    yellow: number;
    red: number;
    total: number;
  };
  live_diff: LiveDiff | null;
  /** When true, publish is blocked until every indicator is accepted. */
  require_approval_before_publishing?: boolean;
}

export interface ApiDomain {
  id: string;
  scorecard_id: string;
  name: string;
  weight: number;
  domain_score: number | null;
  traffic_light: TrafficLight;
  color: string;
  sort_order: number;
  description?: string | null;
  benchmark_upper?: number | null;
  benchmark_lower?: number | null;
}

export interface ApiIndicator {
  id: string;
  domain_id: string;
  name: string;
  value_source: ValueSource;
  linked_formula_id: string | null;
  current_value: number | null;
  normalised_score: number | null;
  value_scale: ValueScale;
  scale_min?: number | null;
  scale_max?: number | null;
  weight: number;
  target_value: number | null;
  benchmark_upper: number | null;
  benchmark_lower: number | null;
  benchmark_source: BenchmarkSource;
  linked_benchmark_id: string | null;
  external_benchmark_label: string | null;
  external_benchmark_url: string | null;
  dynamic_range: DynamicRangeBand[] | null;
  current_band: string | null;
  traffic_light: TrafficLight;
  update_mode: UpdateMode;
  notes?: string | null;
  approval_status: ApprovalStatus;
  /** Dimensions this indicator is tagged to. `[]` when not disaggregated. */
  breakdowns?: {
    id: string;
    dimension_id: string;
    source: string;
    last_updated_at: string;
    values: { category_id: string; value: number | string | null }[];
  }[];
  approval_note: string | null;
  approved_by: number | null;
  approved_at: string | null;
  last_updated_at: string | null;
  sort_order: number;
}

export interface ApiDomainWithIndicators extends ApiDomain {
  indicators: ApiIndicator[];
}

export interface ApiScorecardFull extends ApiScorecard {
  domains: ApiDomainWithIndicators[];
}

// ── Publish ───────────────────────────────────────────────────────────────────

export interface PublishResponse {
  scorecard_id: string;
  status: string;
  version_number: number;
  overall_score: number | null;
  overall_traffic_light: TrafficLight;
  published_at: string;
  published_by: number;
  pinned: boolean | null;
}

// ── History ───────────────────────────────────────────────────────────────────

export interface HistorySnapshot {
  id: string;
  version_number: number;
  version_label: string;
  overall_score: number | null;
  overall_traffic_light: TrafficLight;
  overall_delta: number | null;
  published_at: string;
  published_by: { id: number; name: string };
  domains: Array<{
    name: string;
    domain_score: number | null;
    traffic_light: TrafficLight;
    color: string;
    delta: number | null;
  }>;
}

export interface HistoryTrendPoint {
  version_number: number;
  version_label: string;
  overall_score: number | null;
  published_at: string;
  domains: Array<{ name: string; domain_score: number | null; color: string }>;
}

export interface HistoryListResponse {
  current_version: number;
  snapshots: HistorySnapshot[];
  trend: HistoryTrendPoint[];
}

export interface HistorySnapshotFull {
  id: string;
  version_number: number;
  version_label: string;
  overall_score: number | null;
  overall_traffic_light: TrafficLight;
  published_at: string;
  published_by: { id: number; name: string };
  // Real response: { title, domains, scoring_mode, overall_score, overall_traffic_light }.
  snapshot_data: ApiDomainWithIndicators[] | { domains: ApiDomainWithIndicators[]; [key: string]: unknown };
}

export interface CompareResponse {
  v1: { id: string; version_number: number; overall_score: number | null };
  v2: { id: string; version_number: number; overall_score: number | null };
  overall_delta: number | null;
  domains: Array<{
    name: string;
    score_v1: number | null;
    score_v2: number | null;
    delta: number | null;
    traffic_light_v1: TrafficLight;
    traffic_light_v2: TrafficLight;
    indicators: Array<{
      name: string;
      value_v1: number | null;
      value_v2: number | null;
      delta: number | null;
      traffic_light_v1: TrafficLight;
      traffic_light_v2: TrafficLight;
    }>;
  }>;
}

// ── Templates ─────────────────────────────────────────────────────────────────

export interface ApiTemplate {
  id: string;
  name: string;
  best_for: string;
  domain_count: number;
}

export interface ApiTemplateDetail extends ApiTemplate {
  template_data: {
    domains: Array<{
      name: string;
      weight: number;
      color: string;
      indicators: Array<{ name: string; weight?: number }>;
    }>;
  };
}

// ── Benchmark library ─────────────────────────────────────────────────────────

export interface ApiBenchmarkLibraryEntry {
  id: string;
  name: string;
  description?: string | null;
  category?: string | null;
  value_scale?: ValueScale | null;
  benchmark_lower?: number | null;
  benchmark_upper?: number | null;
  target_value?: number | null;
  source_label?: string | null;
  source_url?: string | null;
  is_active: boolean;
}

// ── Linkable formulas ─────────────────────────────────────────────────────────

export interface LinkableFormula {
  id: string;
  name: string;
  status: string;
  is_named_result: boolean;
  last_result: number | null;
  updated_at: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// API FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────

// ── Scorecard ─────────────────────────────────────────────────────────────────

export const getScorecard = async (
  scorecardId: string,
): Promise<ApiScorecardFull | null> => {
  try {
    const { data } = await api.get(`/bsc/scorecards/${scorecardId}`);
    return data;
  } catch (err: any) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
};

export const createScorecard = async (
  projectId: string,
  payload: {
    title: string;
    scoring_mode?: ScoringMode;
    template_id?: string;
    overall_benchmark_upper?: number;
    overall_benchmark_lower?: number;
  },
): Promise<ApiScorecardFull> => {
  const { data } = await api.post(
    `/bsc/projects/${projectId}/scorecards`,
    payload,
  );
  return data;
};

export const updateScorecard = async (
  scorecardId: string,
  payload: {
    title?: string;
    description?: string;
    scoring_mode?: ScoringMode;
    overall_benchmark_upper?: number | null;
    overall_benchmark_lower?: number | null;
    require_approval_before_publishing?: boolean;
  },
): Promise<ApiScorecard> => {
  const { data } = await api.patch(`/bsc/scorecards/${scorecardId}`, payload);
  return data;
};

export const publishScorecard = async (
  scorecardId: string,
  payload: { note?: string; pin?: boolean },
): Promise<PublishResponse> => {
  const { data } = await api.post(
    `/bsc/scorecards/${scorecardId}/publish`,
    payload,
  );
  return data;
};

export const exportToDashboard = async (
  scorecardId: string,
  pin = true,
): Promise<{
  scorecard_id: string;
  dashboard_card_id: string;
  pinned: boolean;
}> => {
  const { data } = await api.post(
    `/bsc/scorecards/${scorecardId}/export-to-dashboard`,
    { pin },
  );
  return data;
};

// ── Domains ───────────────────────────────────────────────────────────────────

export const createDomain = async (
  scorecardId: string,
  payload: {
    name: string;
    weight?: number;
    color?: string;
    description?: string;
    benchmark_upper?: number;
    benchmark_lower?: number;
    sort_order?: number;
  },
): Promise<ApiDomain> => {
  const { data } = await api.post(
    `/bsc/scorecards/${scorecardId}/domains`,
    payload,
  );
  return data;
};

export const updateDomain = async (
  scorecardId: string,
  domainId: string,
  payload: {
    name?: string;
    weight?: number;
    color?: string;
    description?: string;
    benchmark_upper?: number | null;
    benchmark_lower?: number | null;
    sort_order?: number;
  },
): Promise<ApiDomain> => {
  const { data } = await api.patch(
    `/bsc/scorecards/${scorecardId}/domains/${domainId}`,
    payload,
  );
  return data;
};

export const deleteDomain = async (
  scorecardId: string,
  domainId: string,
  options?: { reassign_to?: string; force?: boolean },
): Promise<void> => {
  await api.delete(`/bsc/scorecards/${scorecardId}/domains/${domainId}`, {
    params: options,
  });
};

// ── Indicators ────────────────────────────────────────────────────────────────

export const createIndicator = async (
  domainId: string,
  payload: {
    name: string;
    value_source?: ValueSource;
    linked_formula_id?: string | null;
    current_value?: number | null;
    value_scale?: ValueScale;
    scale_min?: number;
    scale_max?: number;
    weight?: number;
    target_value?: number | null;
    benchmark_upper?: number | null;
    benchmark_lower?: number | null;
    benchmark_source?: BenchmarkSource;
    linked_benchmark_id?: string | null;
    dynamic_range?: DynamicRangeBand[] | null;
    update_mode?: UpdateMode;
    notes?: string | null;
  },
): Promise<ApiIndicator> => {
  const { data } = await api.post(
    `/bsc/domains/${domainId}/indicators`,
    payload,
  );
  return data;
};

export const updateIndicator = async (
  domainId: string,
  indicatorId: string,
  payload: {
    name?: string;
    value_source?: ValueSource;
    linked_formula_id?: string | null;
    current_value?: number | null;
    value_scale?: ValueScale;
    scale_min?: number | null;
    scale_max?: number | null;
    weight?: number;
    target_value?: number | null;
    benchmark_upper?: number | null;
    benchmark_lower?: number | null;
    benchmark_source?: BenchmarkSource;
    linked_benchmark_id?: string | null;
    dynamic_range?: DynamicRangeBand[] | null;
    update_mode?: UpdateMode;
    notes?: string | null;
  },
): Promise<ApiIndicator> => {
  const { data } = await api.patch(
    `/bsc/domains/${domainId}/indicators/${indicatorId}`,
    payload,
  );
  return data;
};

export const deleteIndicator = async (
  domainId: string,
  indicatorId: string,
): Promise<void> => {
  await api.delete(`/bsc/domains/${domainId}/indicators/${indicatorId}`);
};

export const setIndicatorApproval = async (
  domainId: string,
  indicatorId: string,
  payload: { approval_status: ApprovalStatus; approval_note?: string },
): Promise<ApiIndicator> => {
  const { data } = await api.patch(
    `/bsc/domains/${domainId}/indicators/${indicatorId}/approval`,
    payload,
  );
  return data;
};

export const updateIndicatorBenchmarks = async (
  indicatorId: string,
  payload: {
    benchmark_lower?: number | null;
    benchmark_upper?: number | null;
    target_value?: number | null;
    benchmark_source?: BenchmarkSource;
    linked_benchmark_id?: string | null;
    external_benchmark_label?: string | null;
    external_benchmark_url?: string | null;
  },
): Promise<ApiIndicator> => {
  const { data } = await api.patch(
    `/bsc/indicators/${indicatorId}/benchmarks`,
    payload,
  );
  return data;
};

// ── History ───────────────────────────────────────────────────────────────────

export const getScorecardHistory = async (
  scorecardId: string,
): Promise<HistoryListResponse> => {
  const { data } = await api.get(`/bsc/scorecards/${scorecardId}/history`);
  return data;
};

export const getHistorySnapshot = async (
  scorecardId: string,
  versionId: string,
): Promise<HistorySnapshotFull> => {
  const { data } = await api.get(
    `/bsc/scorecards/${scorecardId}/history/${versionId}`,
  );
  return data;
};

export const compareSnapshots = async (
  scorecardId: string,
  v1: string,
  v2: string,
): Promise<CompareResponse> => {
  const { data } = await api.get(
    `/bsc/scorecards/${scorecardId}/history/compare`,
    { params: { v1, v2 } },
  );
  return data;
};

// ── Templates ─────────────────────────────────────────────────────────────────

export const getTemplates = async (): Promise<ApiTemplate[]> => {
  const { data } = await api.get(`/bsc/bsc/templates`);
  return Array.isArray(data?.templates) ? data.templates : [];
};

export const getTemplateDetail = async (
  templateId: string,
): Promise<ApiTemplateDetail> => {
  const { data } = await api.get(`/bsc/bsc/templates/${templateId}`);
  return data;
};

// ── Benchmark library ─────────────────────────────────────────────────────────

export const getBenchmarkLibrary = async (
  projectId: string,
  params?: { search?: string },
): Promise<ApiBenchmarkLibraryEntry[]> => {
  const { data } = await api.get(`/library/projects/${projectId}/references`, {
    params: { category: "benchmark_value", type: "user_created", ...params },
  });
  const items: any[] = Array.isArray(data) ? data : (data.items ?? []);
  return items.map((r) => ({
    id: r.reference_id ?? r.id,
    name: r.title,
    description: r.description ?? null,
    category: r.category ?? null,
    value_scale: null,
    benchmark_lower: null,
    benchmark_upper: null,
    target_value: r.value?.value ?? null,
    source_label: r.citation ?? null,
    source_url: r.value?.source_url ?? null,
    is_active: true,
  }));
};

// ── Linkable formulas ─────────────────────────────────────────────────────────

export const getLinkableFormulas = async (
  scorecardId: string,
  isNamed = true,
): Promise<LinkableFormula[]> => {
  const { data } = await api.get(
    `/bsc/scorecards/${scorecardId}/linkable-formulas`,
    { params: { is_named: isNamed } },
  );
  return Array.isArray(data?.formulas) ? data.formulas : [];
};

// ── List scorecards ──────────────────────────────────────────────────────────

export interface ApiScorecardSummary {
  id: string;
  project_id: string;
  title: string;
  description?: string;
  scoring_mode: ScoringMode;
  status: ScorecardStatus;
  overall_score: number | null;
  overall_traffic_light: TrafficLight;
  domain_count: number;
  indicator_count: number;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  summary?: {
    green: number;
    yellow: number;
    red: number;
    total: number;
  };
}

export const listScorecards = async (
  projectId: string,
  skip: number = 0,
  limit: number = 10,
): Promise<{ scorecards: ApiScorecardSummary[]; total: number }> => {
  const { data } = await api.get(`/bsc/projects/${projectId}/scorecards`, {
    params: { skip, limit },
  });
  return {
    scorecards: Array.isArray(data?.scorecards) ? data.scorecards : [],
    total: data?.total ?? 0,
  };
};

export const deleteScorecard = async (
  scorecardId: string,
): Promise<{ deleted: boolean; id: string; reassigned_indicators: number }> => {
  const { data } = await api.delete(`/bsc/scorecards/${scorecardId}`);
  return data;
};

// ── Share endpoints ──────────────────────────────────────────────────────────

export type ShareInfo = {
  id: string;
  scorecard_id: string;
  share_token: string;
  shared_url: string;
  is_active: boolean;
  created_by: number;
  created_at: string;
  updated_at: string;
};

export const getShareInfo = async (scorecardId: string): Promise<ShareInfo> => {
  const { data } = await api.get(`/bsc/scorecards/${scorecardId}/share`);
  return data;
};

export const createShare = async (scorecardId: string): Promise<ShareInfo> => {
  const { data } = await api.post(`/bsc/scorecards/${scorecardId}/share`);
  return data;
};

export const toggleShare = async (
  scorecardId: string,
  is_active: boolean,
): Promise<ShareInfo> => {
  const { data } = await api.patch(`/bsc/scorecards/${scorecardId}/share`, {
    is_active,
  });
  return data;
};

export const regenerateShare = async (
  scorecardId: string,
): Promise<ShareInfo> => {
  const { data } = await api.post(
    `/bsc/scorecards/${scorecardId}/share/regenerate`,
  );
  return data;
};

export const getPublicBalancedScorecard = async (
  shareToken: string,
): Promise<{ scorecard: ApiScorecardFull; history: HistoryListResponse }> => {
  const { data } = await api.get(`/bsc/public/scorecards/${shareToken}`);
  return data;
};

export const comparePublicVersions = async (
  shareToken: string,
  v1: string,
  v2: string,
): Promise<CompareResponse> => {
  const { data } = await api.get(`/bsc/public/scorecards/${shareToken}/history/compare`, {
    params: { v1, v2 },
  });
  return data;
};

// ── Dimensions (disaggregation) ───────────────────────────────────────────────

export interface ApiDimensionCategory {
  id: string;
  name: string;
  sort_order: number;
}

export interface ApiDimension {
  id: string;
  scorecard_id: string;
  name: string;
  sort_order: number;
  categories: ApiDimensionCategory[];
  indicator_count: number;
  created_at: string;
  updated_at: string;
}

/** Body item for PUT …/categories. Omit `id` to create a new category. */
export interface DimensionCategoryInput {
  id?: string;
  name: string;
}

export const listDimensions = async (
  scorecardId: string,
): Promise<ApiDimension[]> => {
  const { data } = await api.get(`/bsc/scorecards/${scorecardId}/dimensions`);
  return data.dimensions;
};

export const createDimension = async (
  scorecardId: string,
  payload: { name: string; categories?: string[] },
): Promise<ApiDimension> => {
  const { data } = await api.post(
    `/bsc/scorecards/${scorecardId}/dimensions`,
    payload,
  );
  return data;
};

export const updateDimension = async (
  scorecardId: string,
  dimensionId: string,
  payload: { name?: string; sort_order?: number },
): Promise<ApiDimension> => {
  const { data } = await api.patch(
    `/bsc/scorecards/${scorecardId}/dimensions/${dimensionId}`,
    payload,
  );
  return data;
};

/** Throws the axios error on 409 `bsc_dimension_in_use`; retry with force=true. */
export const deleteDimension = async (
  scorecardId: string,
  dimensionId: string,
  force = false,
): Promise<void> => {
  await api.delete(
    `/bsc/scorecards/${scorecardId}/dimensions/${dimensionId}`,
    { params: { force } },
  );
};

/**
 * Replaces the whole category list in one call. Throws on 409
 * `bsc_categories_have_values`; retry with force=true.
 */
export const replaceDimensionCategories = async (
  scorecardId: string,
  dimensionId: string,
  categories: DimensionCategoryInput[],
  force = false,
): Promise<ApiDimension> => {
  const { data } = await api.put(
    `/bsc/scorecards/${scorecardId}/dimensions/${dimensionId}/categories`,
    { categories },
    { params: { force } },
  );
  return data;
};

// ── Alerts ────────────────────────────────────────────────────────────────────

export type AlertType = "publish_notification" | "weekly_digest" | "threshold";
export type AlertScopeType = "all" | "domains" | "indicators";
export type AlertDirection = "decreases" | "increases";

export interface AlertChannels {
  in_app: boolean;
  email: boolean;
}

export interface AlertRecipient {
  user_id: number;
  name: string;
  email: string;
  has_access: boolean;
}

export interface AlertSummary {
  watches: string | null;
  when: string | null;
  notifies: string;
  via: string;
}

export interface ApiAlertRule {
  id: string;
  scorecard_id: string;
  alert_type: AlertType;
  name: string;
  is_default: boolean;
  is_enabled: boolean;
  can_delete: boolean;
  scope_type: AlertScopeType;
  direction: AlertDirection | null;
  schedule: { day: number; time: string } | null;
  channels: AlertChannels;
  // Assumed to be id arrays; the API doc only shows `[]`. Check against a real response.
  // Returned as objects by GET /alerts (confirmed on dev). Send only `id` back.
  domains: { id: string; name: string; domain_id?: string | null; domain_name?: string | null }[];
  indicators: { id: string; name: string; domain_id?: string | null; domain_name?: string | null }[];
  recipients: AlertRecipient[];
  resolved_recipient_count: number;
  summary: AlertSummary;
  created_at: string;
  updated_at: string;
}

export interface AlertWritePayload {
  alert_type?: AlertType;
  name?: string | null;
  scope_type?: AlertScopeType;
  domain_ids?: string[];
  indicator_ids?: string[];
  direction?: AlertDirection;
  schedule?: { day: number; time: string };
  recipient_user_ids?: number[];
  channels?: AlertChannels;
  is_enabled?: boolean;
}

export interface AlertScopeOptions {
  domains: { id: string; name: string }[];
  indicators: {
    id: string;
    name: string;
    domain_id: string;
    domain_name: string;
  }[];
}

export const listAlerts = async (
  scorecardId: string,
): Promise<ApiAlertRule[]> => {
  const { data } = await api.get(`/bsc/scorecards/${scorecardId}/alerts`);
  return data.alerts;
};

export const createAlert = async (
  scorecardId: string,
  payload: AlertWritePayload,
): Promise<ApiAlertRule> => {
  const { data } = await api.post(
    `/bsc/scorecards/${scorecardId}/alerts`,
    payload,
  );
  return data;
};

/** Partial update. Only the fields you send change. */
export const updateAlert = async (
  scorecardId: string,
  ruleId: string,
  payload: AlertWritePayload,
): Promise<ApiAlertRule> => {
  const { data } = await api.patch(
    `/bsc/scorecards/${scorecardId}/alerts/${ruleId}`,
    payload,
  );
  return data;
};

export const deleteAlert = async (
  scorecardId: string,
  ruleId: string,
): Promise<void> => {
  await api.delete(`/bsc/scorecards/${scorecardId}/alerts/${ruleId}`);
};

export const getAlertScopeOptions = async (
  scorecardId: string,
): Promise<AlertScopeOptions> => {
  const { data } = await api.get(
    `/bsc/scorecards/${scorecardId}/alerts/scope-options`,
  );
  return data;
};

export const searchAlertRecipients = async (
  scorecardId: string,
  q: string,
  limit = 10,
): Promise<AlertRecipient[]> => {
  const { data } = await api.get(
    `/bsc/scorecards/${scorecardId}/alerts/recipient-search`,
    { params: { q, limit } },
  );
  return data.results;
};

export const previewAlertRecipients = async (
  scorecardId: string,
  extraUserIds: number[],
): Promise<{ count: number; recipients: AlertRecipient[] }> => {
  // Build the repeated key by hand: axios would send `extra_user_ids[]=`.
  const query = extraUserIds
    .map((id) => `extra_user_ids=${encodeURIComponent(id)}`)
    .join("&");
  const { data } = await api.get(
    `/bsc/scorecards/${scorecardId}/alerts/recipient-preview${query ? `?${query}` : ""}`,
  );
  return data;
};

// ── Bulk entry ────────────────────────────────────────────────────────────────

export interface BulkEntryIndicator {
  id: string;
  name: string;
  value_scale: ValueScale;
  tagged: boolean;
  source: ValueSource | null;
  /** Keyed by category id. `null` = no data. */
  values: Record<string, number | null>;
}

export interface BulkEntryResponse {
  scorecard_id: string;
  dimension: { id: string; name: string };
  categories: { id: string; name: string }[];
  domains: {
    id: string;
    name: string;
    indicators: BulkEntryIndicator[];
  }[];
}

export interface BulkEntryCell {
  indicator_id: string;
  category_id: string;
  value: number | null;
}

export const getBulkEntry = async (
  scorecardId: string,
  dimensionId: string,
): Promise<BulkEntryResponse> => {
  const { data } = await api.get(`/bsc/scorecards/${scorecardId}/bulk-entry`, {
    params: { dimension_id: dimensionId, include_untagged: true },
  });
  return data;
};

/** Send only the cells that changed. Cleared cells use `value: null`. */
export const saveBulkEntry = async (
  scorecardId: string,
  dimensionId: string,
  cells: BulkEntryCell[],
): Promise<{
  dimension_id: string;
  updated: number;
  cleared: number;
  newly_tagged: number;
}> => {
  const { data } = await api.put(`/bsc/scorecards/${scorecardId}/bulk-entry`, {
    dimension_id: dimensionId,
    cells,
  });
  return data;
};

// ── Breakdown by dimension (View by) ──────────────────────────────────────────

export interface BreakdownCell {
  value: number | null;
  normalised_score: number | null;
  traffic_light: TrafficLight;
  band: { from: number; to: number; label: string; color: string } | null;
}

export interface BreakdownIndicator {
  id: string;
  name: string;
  weight: number;
  value_scale: ValueScale;
  current_value: number | null;
  normalised_score: number | null;
  traffic_light: TrafficLight;
  /** false = the indicator is not tagged to this dimension. */
  disaggregated: boolean;
  /** Keyed by category id. `null` = no data for that category. */
  cells: Record<string, BreakdownCell | null>;
}

export interface BreakdownResponse {
  scorecard_id: string;
  version_id: string | null;
  version_number: number | null;
  dimension: { id: string; name: string };
  categories: { id: string; name: string }[];
  domains: {
    id: string;
    name: string;
    color: string;
    weight: number;
    domain_score: number | null;
    traffic_light: TrafficLight;
    rollup: Record<string, { normalised_score: number | null; traffic_light: TrafficLight; indicator_count: number } | null>;
    indicators: BreakdownIndicator[];
  }[];
}

export const getBreakdown = async (
  scorecardId: string,
  dimensionId: string,
  versionId?: string,
): Promise<BreakdownResponse> => {
  const { data } = await api.get(
    `/bsc/scorecards/${scorecardId}/breakdown`,
    {
      params: {
        dimension_id: dimensionId,
        ...(versionId ? { version_id: versionId } : {}),
      },
    },
  );
  return data;
};
